import axios from 'axios';
import config from '../config';

// Create an axios instance with default config
const APIClient = axios.create({
  baseURL: config.baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a request interceptor to add the auth token to every request
APIClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: when the server rejects our token (expired / revoked /
// invalid), tear down the session and bounce to /login. This is the real
// safety net — the server is authoritative, so a 401 means the client session
// is dead regardless of what localStorage says. Auth endpoints are excluded so
// the multi-endpoint login probe (which expects 401s) isn't disrupted.
APIClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isAuthCall = url.includes('/authentication/');
    if (status === 401 && !isAuthCall) {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

export const fetchBestsellers = async (page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getAllProductsFromBestSeller', {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addBestseller = async (pharmacode) => {
  try {
    const response = await APIClient.post(`/addProductToBestseller/${pharmacode}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeBestseller = async (pharmacode) => {
  try {
    const response = await APIClient.post(`/removeProductFromBestseller/${pharmacode}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    // Try different authentication endpoints in order of preference
    const endpoints = [
      '/authentication/login',           // Generic login endpoint
      '/authentication/admin/login',     // Admin login endpoint
      '/authentication/user/login',      // User login endpoint
      '/authentication/client/login'     // Client login endpoint (fallback)
    ];
    
    let lastError = null;
    
    for (const endpoint of endpoints) {
      try {
        const response = await APIClient.post(endpoint, credentials);
        
        // Check all possible token locations in the response
        const token = response.data?.token || 
                     response.data?.data?.token || 
                     response.data?.accessToken ||
                     response.data?.data?.accessToken;
        
        if (token) {
          // Store the token
          localStorage.setItem('token', token);
          
          // Determine user role based on which endpoint was successful
          let userRole;
          if (endpoint.includes('/admin/login')) {
            userRole = 1; // ADMIN
          } else if (endpoint.includes('/client/login')) {
            userRole = 3; // CLIENT
          } else if (endpoint.includes('/user/login')) {
            userRole = 2; // USER
          } else {
            userRole = 1; // Default to ADMIN for generic login
          }
          
          // Create user data object with role information
          const userData = {
            role: userRole,
            email: credentials.email,
            loginEndpoint: endpoint
          };
          
          // Store user data in localStorage
          localStorage.setItem('userData', JSON.stringify(userData));

          // Update the default Authorization header for future requests
          APIClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Return the full response data
          return response.data;
        } else {
          throw new Error('No token received from server');
        }
      } catch (endpointError) {
        lastError = endpointError;
        
        // Check for role-specific restriction errors and try next endpoint
        if (endpointError.response?.status === 401) {
          const errorMessage = endpointError.response?.data?.error;
          
          // If this is a role restriction error, try next endpoint
          if (errorMessage === 'You can only log in as a client!' ||
              errorMessage === 'You can only log in as an admin!' ||
              errorMessage === 'You can only log in as a user!' ||
              errorMessage?.includes('You can only log in as')) {
            continue;
          }
        }
        
        // If this is a different error (like 404 for endpoint not found), try next endpoint
        if (endpointError.response?.status === 404) {
          continue;
        }
        
        // For other errors, break and throw the error
        throw endpointError;
      }
    }
    
    // If we get here, all endpoints failed
    throw lastError || new Error('All login endpoints failed');
    
  } catch (error) {
    console.error('Login failed. Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const checkLoginStatus = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

export const logoutUser = () => {
  // Clear the whole session, not just the token — leaving userData behind kept a
  // stale role/identity around after logout.
  localStorage.removeItem('token');
  localStorage.removeItem('userData');
};

/**
 * Apply a discount rule to a pharmacode.
 *
 * @param {number|string} pharmacode
 * @param {object} rule
 * @param {'percent'|'fixed'} rule.kind
 * @param {number} rule.amount         percent (0-99) or fixed CHF price
 * @param {string} [rule.startsAt]     ISO date — when the discount becomes active
 * @param {string} [rule.endsAt]       ISO date — when it expires
 * @param {string} [rule.label]        optional human label
 *
 * Backwards-compatible signature: `updateProductDiscount(pharmacode, 12.50)`
 * still works (treated as { kind: 'fixed', amount: 12.50 }).
 */
export const updateProductDiscount = async (pharmacode, ruleOrPrice) => {
  const body =
    typeof ruleOrPrice === 'number' || typeof ruleOrPrice === 'string'
      ? { kind: 'fixed', amount: Number(ruleOrPrice) }
      : ruleOrPrice;
  try {
    const response = await APIClient.put(`/${pharmacode}/discount`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeProductDiscount = async (pharmacode) => {
  try {
    const response = await APIClient.delete(`/${pharmacode}/discount`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ── Discount import (bulk-from-file) ─────────────────────────────────
// Upload a Verfalldaten PDF (or any future supported format), parse it on
// the backend, get a preview of matched / skipped / blocked rows, then
// commit. See controllers/discountImports.js.

/**
 * Upload a file and get a preview. `modeCfg` is the rule-mode object,
 * e.g. { mode: 'flatPercent', percent: 30 }, or
 *      { mode: 'tieredByExpiry', tiers: [{ maxMonths: 3, percent: 50 }, ...] }
 *      { mode: 'flatFixedOff', chfOff: 5 }
 *      { mode: 'perRow' }
 * Optional global props: globalStartsAt, globalEndsAt, globalLabel.
 */
export const createDiscountImportPreview = async (file, modeCfg, opts = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('mode', JSON.stringify(modeCfg));
  if (opts.globalStartsAt) fd.append('globalStartsAt', opts.globalStartsAt);
  if (opts.globalEndsAt)   fd.append('globalEndsAt',   opts.globalEndsAt);
  if (opts.globalLabel)    fd.append('globalLabel',    opts.globalLabel);
  const response = await APIClient.post('/discount-imports', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const fetchDiscountImports = async (page = 1, limit = 20, status = null) => {
  const params = { page, limit };
  if (status) params.status = status;
  const response = await APIClient.get('/discount-imports', { params });
  return response.data;
};

export const fetchDiscountImport = async (id) => {
  const response = await APIClient.get(`/discount-imports/${id}`);
  return response.data;
};

/**
 * Edit one or more row proposals before committing.
 * `proposals` is an array of { pharmacode|rawCode, kind, amount }.
 */
export const updateDiscountImportProposals = async (id, proposals) => {
  const response = await APIClient.patch(`/discount-imports/${id}/proposals`, { proposals });
  return response.data;
};

export const commitDiscountImport = async (id) => {
  const response = await APIClient.post(`/discount-imports/${id}/commit`);
  return response.data;
};

export const cancelDiscountImport = async (id) => {
  const response = await APIClient.delete(`/discount-imports/${id}`);
  return response.data;
};

export const fetchAllDiscounts = async (page = 1, limit = 20) => {
  try {
    console.log('🌐 API Call - fetchAllDiscounts (legacy):', {
      url: '/getAllDiscountProduct',
      params: { page, limit }
    });
    
    const response = await APIClient.get('/getAllDiscountProduct', {
      params: { page, limit }
    });
    
    console.log('📡 API Response - fetchAllDiscounts (legacy):', {
      status: response.status,
      data: response.data,
      success: response.data?.success,
      products: response.data?.data?.products,
      productsLength: response.data?.data?.products?.length
    });
    
    return response.data;
  } catch (error) {
    console.error('💥 API Error - fetchAllDiscounts (legacy):', error);
    console.log('📋 Legacy API Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        params: error.config?.params
      }
    });
    throw error;
  }
};

export const fetchAllUsers = async (page = 1, limit = 10) => {
  try {
    const response = await APIClient.get('/authentication/admin/users', {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const removeUser = async (userId, reason) => {
  try {
    const response = await APIClient.delete(`/authentication/admin/users/${userId}/remove`, {
      data: { reason }
    });
    return response.data;
  } catch (error) {
    console.error('Error removing user:', error);
    // Improve error handling to match backend response format
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { message: 'Failed to delete user' };
  }
};
export const bulkRemoveUsers = async (userIds, reason) => {
  try {
    const response = await APIClient.post('/admin/users/bulk-remove', {
      userIds,
      reason
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk removing users:', error);
    throw error;
  }
};

export const signupClient = async (userData) => {
  try {
    // Format the date properly for the backend
    const formattedData = {
      ...userData,
      role: 3, // CLIENT role number
      birthdate: new Date(userData.birthdate).toISOString() // Convert to ISO string
    };

    
    const response = await APIClient.post('/authentication/signup', formattedData);
    return response.data;
  } catch (error) {
    console.error('Error signing up client:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);
    throw error;
  }
};

export const updateProductPrice = async (pharmacode, newPrice) => {
  try {
    // Ensure pharmacode is a string and log the request
    const pharmacodeStr = String(pharmacode);
    
    // Use the correct endpoint that matches the backend controller
    const response = await APIClient.put(`/admin/products/${pharmacodeStr}/public-price`, { newPrice });
    return response.data;
  } catch (error) {
    console.error('Error updating product price:', error);
    throw error;
  }
};

export const deleteProduct = async (pharmacode, reason) => {
  try {
    const response = await APIClient.delete(`/admin/products/${pharmacode}`, {
      data: { reason }
    });
    return response.data;
  } catch (error) {
    console.error('Single delete error:', error);
    throw error;
  }
};

// ── Manual ("from scratch") products ────────────────────────────────────────
// Create / edit products that aren't in HCI/Galexis. The backend stores them in
// the same collection tagged source:'manual' and protects them from the importer.

export const createCustomProduct = async (payload) => {
  try {
    const response = await APIClient.post('/admin/products', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating custom product:', error);
    throw error;
  }
};

export const updateCustomProduct = async (pharmacode, payload) => {
  try {
    const response = await APIClient.put(`/admin/products/${String(pharmacode)}`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating custom product:', error);
    throw error;
  }
};

// Uploads a single product image; resolves to the relative path to store in
// the product's images array (e.g. "products/product-123-456.jpg").
export const uploadProductImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    const response = await APIClient.post('/admin/products/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading product image:', error);
    throw error;
  }
};

export const bulkDeleteProducts = async (pharmacodes, reason) => {
  try {
    // Convert array to comma-separated string
    const pharmacodesString = Array.isArray(pharmacodes) ? pharmacodes.join(',') : pharmacodes;
    
    const response = await APIClient.delete(`/admin/products/${pharmacodesString}`, {
      data: { reason }
    });
    return response.data;
  } catch (error) {
    console.error('Bulk delete error:', error);
    throw error;
  }
};

export const fetchAllProducts = async (page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getAllProducts', {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error in fetchAllProducts:', error);
    throw error;
  }
};

// Unified products endpoint for selecting all products across all pages
export const fetchUnifiedProducts = async (params = {}) => {
  try {
    const response = await APIClient.get('/admin/products/unified', {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error in fetchUnifiedProducts:', error);
    throw error;
  }
};

// Fetch admin "unpriced" rescue list using the unified endpoint.
// Backed by /admin/products/unified?onlyUnpriced=true which uses the
// relaxed inclusion filter on the backend and excludes any product with
// a usable basePrice or adminRetailPrice. After admin sets a retail
// price via updateProductPrice, the product moves out of this list and
// becomes visible in the customer shop on next refresh.
export const fetchUnpricedProducts = async (page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/admin/products/unified', {
      params: {
        onlyUnpriced: 'true',
        page,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error in fetchUnpricedProducts:', error);
    throw error;
  }
};

// Fetch bestseller products using the unified endpoint
export const fetchBestsellerProducts = async (page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/admin/products/unified', {
      params: { 
        bestseller: 'true',
        page,
        limit
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error in fetchBestsellerProducts:', error);
    throw error;
  }
};

// Fetch discount products using the unified endpoint
export const fetchDiscountProducts = async (page = 1, limit = 20) => {
  try {
    console.log('🌐 API Call - fetchDiscountProducts:', {
      url: '/admin/products/unified',
      params: { 
        discount: 'true',
        page,
        limit
      }
    });
    
    const response = await APIClient.get('/admin/products/unified', {
      params: { 
        discount: 'true',
        page,
        limit
      }
    });
    
    console.log('📡 API Response - fetchDiscountProducts:', {
      status: response.status,
      data: response.data,
      success: response.data?.success,
      items: response.data?.items,
      itemsLength: response.data?.items?.length
    });
    
    return response.data;
  } catch (error) {
    console.error('💥 API Error - fetchDiscountProducts:', error);
    console.log('📋 API Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        params: error.config?.params
      }
    });
    throw error;
  }
};

export const searchProducts = async (searchTerm, page = 1, limit = 20, categoryFilter = '') => {
  try {
    const params = {
      searchTerms: searchTerm,
      page,
      limit,
    };
    
    // Add categoryFilter to params if provided
    if (categoryFilter) {
      params.categoryFilter = categoryFilter;
    }
    
    const response = await APIClient.get('/search', {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error searching products:', error.response ? error.response.data : error.message);
    throw error;
  }
};

// Fetch products for a curated symptom key (symptom -> categories on the backend).
export const fetchProductsBySymptom = async (symptomKey, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/products/search', {
      params: { symptom: symptomKey, page, limit },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductsByCategory = async (consumerCategoryId, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getProductsByCategory', {
      params: { 
        consumerCategoryId, 
        page, 
        limit 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};


// Add a function to check if user is logged in
export const isLoggedIn = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

// Add a function to get the current token
export const getCurrentToken = () => {
  const token = localStorage.getItem('token');
  return token;
};

// Add a function to get current user information from localStorage
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      return JSON.parse(userData);
    }
    return null;
  } catch (error) {
    console.error('Error getting current user from localStorage:', error);
    return null;
  }
};

// Update the request interceptor to use the new getCurrentToken function
APIClient.interceptors.request.use(
  (config) => {
    const token = getCurrentToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const fetchCategories = async () => {
  try {
    const response = await APIClient.get(`/getCategories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

export const fetchSupplyCategories = async () => {
  try {
    const response = await APIClient.get('/getAllSupplyCategories');
    return response.data;
  } catch (error) {
    console.error('Error fetching supply categories:', error);
    throw error;
  }
};

export const fetchProductsBySupplyCategory = async (code, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getProductsBySupplyCategory', {
      params: { code, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by supply category:', error);
    throw error;
  }
};

export const fetchCatalogProducts = async (page = 1, limit = 20, searchTerms = '', filters = {}) => {
  try {
    // Only send filter params that are actually set, so an empty selection
    // doesn't narrow the query. Supported: supplyCategoryCode, atcCode,
    // sortimentCode, migelCode, genericCode, consumerCategoryId, company.
    const params = { page, limit, searchTerms };
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') params[key] = value;
    }
    const response = await APIClient.get('/getCatalogProducts', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching catalog products:', error);
    throw error;
  }
};


// Bulk add bestsellers
export const addMultipleBestsellers = async (pharmacodes) => {
  // pharmacodes: array of numbers or strings
  const codes = pharmacodes.join(',');
  const response = await APIClient.post(`/addProductToBestseller/${codes}`);
  return response.data;
};

// Fetch product details by pharmacode
export const fetchProductDetails = async (pharmacode) => {
  try {
    const response = await APIClient.get(`/${pharmacode}`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.error(`Product with pharmacode ${pharmacode} not found (404).`);
    } else {
      console.error('Error fetching product details:', error);
    }
    throw error;
  }
};

// Deleted Products Management
export const fetchDeletedProducts = async (page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/admin/products/deleted', {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching deleted products:', error);
    throw error;
  }
};

export const restoreDeletedProduct = async (pharmacodes) => {
  try {
    // Convert array to comma-separated string
    const pharmacodesString = Array.isArray(pharmacodes) ? pharmacodes.join(',') : pharmacodes;
    
    // FIX: Use the correct admin endpoint
    const response = await APIClient.post(`/admin/products/${pharmacodesString}/undo-delete`);
    return response.data;
  } catch (error) {
    console.error('Error restoring deleted products:', error);
    throw error;
  }
};

export const permanentlyDeleteProducts = async () => {
  try {
    const response = await APIClient.delete('/admin/products/cleanup-deleted?immediate=true');
    return response.data;
  } catch (error) {
    console.error('Error permanently deleting products:', error);
    throw error;
  }
};

export const fetchSortimentCodes = async () => {
  try {
    const response = await APIClient.get('/getAllSortimentCodes');
    return response.data;
  } catch (error) {
    console.error('Error fetching sortiment codes:', error);
    throw error;
  }
};

export const fetchProductsBySortimentCode = async (code, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getProductsBySortimentCode', {
      params: { code, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by sortiment code:', error);
    throw error;
  }
};

// MiGeL Management APIs
export const fetchAllMiGeLCodes = async () => {
  try {
    const response = await APIClient.get('/getAllMiGeLCodes');
    return response.data;
  } catch (error) {
    console.error('Error fetching MiGeL codes:', error);
    throw error;
  }
};

export const fetchProductsByMiGeLCode = async (code, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getProductsByMiGeLCode', {
      params: { code, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by MiGeL code:', error);
    throw error;
  }
};

// ATC Management APIs
export const fetchAllATCCodes = async () => {
  try {
    const response = await APIClient.get('/getAllATCCodes');
    return response.data;
  } catch (error) {
    console.error('Error fetching ATC codes:', error);
    throw error;
  }
};

export const fetchProductsByATCCode = async (code, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getProductsByATCCode', {
      params: { code, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by ATC code:', error);
    throw error;
  }
};

// Generika Management APIs
export const fetchAllGenerikaCodes = async () => {
  try {
    const response = await APIClient.get('/getAllGenerikaCodes');
    return response.data;
  } catch (error) {
    console.error('Error fetching Generika codes:', error);
    throw error;
  }
};

export const fetchProductsByGenerikaCode = async (code, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/getProductsByGenerikaCode', {
      params: { code, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by Generika code:', error);
    throw error;
  }
};

export const fetchAllCompanies = async () => {
  try {
    const response = await APIClient.get(`/getAllCompanies`);
    return response.data;
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

export const searchCompanies = async (searchTerm, limit = 10) => {
  try {
    const response = await APIClient.get(`/companies/search`, {
      params: { searchTerm, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching companies:', error);
    throw error;
  }
};

export const fetchProductsByCompany = async (company, page = 1, limit = 20, sort = 'random') => {
  try {
    const response = await APIClient.get(`/getProductsByCompany`, {
      params: { company, page, limit, sort },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching products by company:', error);
    throw error;
  }
};

// Product Import API (import products from a CSV/XLSX file of pharmacodes)
//
// Three-step flow mirroring the discount importer:
//   1. createProductImportPreview(file)  → parse + classify (preview)
//   2. updateProductImportRows(id, rows) → toggle selection / edit PPUB
//   3. commitProductImport(id)           → background engine run (poll getProductImport)

/**
 * Upload a .csv/.xlsx file of pharmacodes and get a classified preview.
 */
export const createProductImportPreview = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const response = await APIClient.post('/product-imports', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const fetchProductImports = async (page = 1, limit = 20, status = null) => {
  const params = { page, limit };
  if (status) params.status = status;
  const response = await APIClient.get('/product-imports', { params });
  return response.data;
};

export const getProductImport = async (id) => {
  const response = await APIClient.get(`/product-imports/${id}`);
  return response.data;
};

/**
 * Edit one or more rows before committing.
 * `rows` is an array of { pharmacode|rawCode, selected?, ppub? }.
 */
export const updateProductImportRows = async (id, rows) => {
  const response = await APIClient.patch(`/product-imports/${id}/rows`, { rows });
  return response.data;
};

export const commitProductImport = async (id) => {
  const response = await APIClient.post(`/product-imports/${id}/commit`);
  return response.data;
};

export const cancelProductImport = async (id) => {
  const response = await APIClient.delete(`/product-imports/${id}`);
  return response.data;
};

// --- Analytics Stats API ---

export const fetchRevenueStats = async (from, to, granularity = 'day') => {
  try {
    const response = await APIClient.get('/analytics/stats/revenue', {
      params: { from, to, granularity },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue stats:', error);
    throw error;
  }
};

export const fetchOrderStats = async (from, to) => {
  try {
    const response = await APIClient.get('/analytics/stats/orders', {
      params: { from, to },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching order stats:', error);
    throw error;
  }
};

// Paginated. Returns { success, data: { products, total, limit, offset } }.
// `offset` and `q` are optional so existing top-N callers keep working unchanged.
export const fetchTopProducts = async (from, to, limit = 10, offset = 0, q = '') => {
  try {
    const response = await APIClient.get('/analytics/stats/top-products', {
      params: { from, to, limit, offset, ...(q ? { q } : {}) },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching top products:', error);
    throw error;
  }
};

export const fetchUserStats = async (from, to, granularity = 'day') => {
  try {
    const response = await APIClient.get('/analytics/stats/users', {
      params: { from, to, granularity },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

export const fetchPageviewStats = async (from, to, granularity = 'day') => {
  try {
    const response = await APIClient.get('/analytics/stats/pageviews', {
      params: { from, to, granularity },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pageview stats:', error);
    throw error;
  }
};

// Paginated. Returns { success, data: { terms, total, limit, offset } }.
// `offset` and `q` are optional so existing top-N callers keep working unchanged.
export const fetchSearchTermStats = async (from, to, limit = 20, offset = 0, q = '') => {
  try {
    const response = await APIClient.get('/analytics/stats/search-terms', {
      params: { from, to, limit, offset, ...(q ? { q } : {}) },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching search terms:', error);
    throw error;
  }
};

export const fetchCountryStats = async (from, to) => {
  try {
    const response = await APIClient.get('/analytics/stats/countries', {
      params: { from, to },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching country stats:', error);
    throw error;
  }
};

// ----- Product engagement (clicks per product, by source / country / Swiss canton) -----
// Every call takes the same drill-down filters:
//   { from, to, source?, country?, region? }   (region = Swiss canton code)
const engagementParams = (filters = {}) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v != null && v !== ''));

export const fetchEngagementOverview = async (filters) => {
  const response = await APIClient.get('/analytics/stats/product-engagement/overview', {
    params: engagementParams(filters),
  });
  return response.data;
};

// Paginated. Extra params: { limit, offset, q, sort: views|visitors|carts|cartRate }.
export const fetchEngagementProducts = async (filters) => {
  const response = await APIClient.get('/analytics/stats/product-engagement/products', {
    params: engagementParams(filters),
  });
  return response.data;
};

export const fetchEngagementProduct = async (pharmacode, filters) => {
  const response = await APIClient.get(
    `/analytics/stats/product-engagement/products/${encodeURIComponent(pharmacode)}`,
    { params: engagementParams(filters) }
  );
  return response.data;
};

// ----- Orders (admin) -----

export const fetchAdminOrders = async ({
  page = 1,
  limit = 20,
  paymentStatus,
  orderStatus,
  search,
  from,
  to,
  sortBy,
  sortDir,
  failedInvoiceOnly,
} = {}) => {
  try {
    const params = { page, limit };
    if (paymentStatus && paymentStatus !== 'all') params.paymentStatus = paymentStatus;
    if (orderStatus && orderStatus !== 'all') params.orderStatus = orderStatus;
    if (search) params.search = search;
    if (from) params.from = from;
    if (to) params.to = to;
    if (sortBy) params.sortBy = sortBy;
    if (sortDir) params.sortDir = sortDir;
    if (failedInvoiceOnly) params.failedInvoiceOnly = true;
    const response = await APIClient.get('/orders/admin/all', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin orders:', error);
    throw error;
  }
};

export const fetchAdminOrderById = async (orderId) => {
  try {
    const response = await APIClient.get(`/orders/admin/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching admin order:', error);
    throw error;
  }
};

export const downloadAdminInvoice = async (orderId, orderNumber) => {
  const response = await APIClient.get(`/orders/admin/${orderId}/invoice`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoice-${orderNumber || orderId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const resendInvoiceEmail = async (orderId) => {
  const response = await APIClient.post(`/orders/admin/${orderId}/resend-invoice`);
  return response.data;
};

export const retryInvoice = async (orderId) => {
  const response = await APIClient.post(`/orders/admin/${orderId}/retry-invoice`);
  return response.data;
};

export const fetchFailedInvoices = async () => {
  const response = await APIClient.get('/orders/admin/failed-invoices');
  return response.data;
};

export const exportInvoices = async (from, to) => {
  const response = await APIClient.get('/orders/admin/export-invoices', {
    params: { from, to },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/zip' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoices-${from}-to-${to}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const fetchFunnelStats = async (from, to) => {
  try {
    const response = await APIClient.get('/analytics/stats/funnel', {
      params: { from, to },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching funnel stats:', error);
    throw error;
  }
};

// ──────────────────────────── Campaigns ────────────────────────────────

export const fetchCampaigns = async (status) => {
  const params = status ? { status } : {};
  const res = await APIClient.get('/campaigns', { params });
  return res.data;
};

export const fetchCampaignBySlug = async (slug) => {
  const res = await APIClient.get(`/campaigns/${encodeURIComponent(slug)}`);
  return res.data;
};

export const createCampaign = async (body) =>
  (await APIClient.post('/campaigns', body)).data;

export const updateCampaign = async (id, body) =>
  (await APIClient.patch(`/campaigns/${id}`, body)).data;

export const deleteCampaign = async (id) =>
  (await APIClient.delete(`/campaigns/${id}`)).data;

export const uploadCampaignBanner = async (file, slug) => {
  const form = new FormData();
  form.append('banner', file);
  if (slug) form.append('slug', slug);
  const res = await APIClient.post('/campaigns/upload-banner', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

/**
 * Preview (dryRun=true) or commit a bulk-attach of products to a campaign.
 * Returns { ready, conflicts, errors } in both modes; commit mode also
 * returns { inserted, replaced, skipped }.
 */
export const bulkAttachToCampaign = async (campaignId, body, dryRun = false) => {
  const url = `/campaigns/${campaignId}/products${dryRun ? '?dryRun=true' : ''}`;
  return (await APIClient.post(url, body)).data;
};

export const removeProductFromCampaign = async (campaignId, pharmacode) =>
  (await APIClient.delete(`/campaigns/${campaignId}/products/${pharmacode}`)).data;

/** Fetch products attached to a campaign (admin view: includes scheduled/ended). */
export const fetchCampaignProducts = async (slug) => {
  const res = await APIClient.get('/getAllDiscountProduct', {
    params: { liveOnly: false, campaign: slug, limit: 200 },
  });
  return res.data;
};



